import React, { useCallback, useState, useEffect, useRef } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Autocomplete,
  Chip,
  IconButton,
  Collapse,
  FormControlLabel,
  Checkbox,
  SelectChangeEvent,
} from "@mui/material";
import {
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  SessionFilters as SessionFiltersType,
  SessionFiltersProps,
  TimeRangePreset,
  OrganizationOption,
  ExperienceOption,
} from "./types";

const TIME_RANGE_OPTIONS: { value: TimeRangePreset | ""; label: string }[] = [
  { value: "", label: "All Time" },
  { value: "last_hour", label: "Last Hour" },
  { value: "last_day", label: "Last 24 Hours" },
  { value: "last_week", label: "Last Week" },
  { value: "last_month", label: "Last Month" },
  { value: "custom", label: "Custom Range" },
];

const STATUS_OPTIONS: { value: string; label: string; color: string }[] = [
  { value: "creating", label: "Creating", color: "#9e9e9e" },
  { value: "ready_to_configure", label: "Ready to Configure", color: "#ff9800" },
  { value: "configuring", label: "Configuring", color: "#ff9800" },
  { value: "ready_to_start", label: "Ready to Start", color: "#2196f3" },
  { value: "in_progress", label: "In Progress", color: "#4caf50" },
  { value: "finished", label: "Finished", color: "#9c27b0" },
  { value: "finalized", label: "Finalized", color: "#673ab7" },
];

const CODEC_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "All Codecs" },
  { value: "H264", label: "H264" },
  { value: "VP8", label: "VP8" },
  { value: "VP9", label: "VP9" },
  { value: "AV1", label: "AV1" },
];

// Debounce delay for text search fields (ms)
const SEARCH_DEBOUNCE_MS = 500;

const SessionFilters: React.FC<SessionFiltersProps> = ({
  filters,
  onFiltersChange,
  organizations,
  assessments,
  roleplays,
  loading = false,
  loadingExperiences = false,
}) => {
  const [expanded, setExpanded] = useState(true);
  const [showCustomDates, setShowCustomDates] = useState(
    filters.timeRangePreset === "custom"
  );

  // Local state for debounced text fields
  const [localUserEmail, setLocalUserEmail] = useState(filters.userEmailSearch || "");
  const [localSessionId, setLocalSessionId] = useState(filters.sessionIdSearch || "");
  const [localConversationId, setLocalConversationId] = useState(filters.conversationIdSearch || "");

  // Refs for debounce timers
  const userEmailTimerRef = useRef<NodeJS.Timeout | null>(null);
  const sessionIdTimerRef = useRef<NodeJS.Timeout | null>(null);
  const conversationIdTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Sync local state when filters change externally (e.g., clear filters)
  useEffect(() => {
    setLocalUserEmail(filters.userEmailSearch || "");
  }, [filters.userEmailSearch]);

  useEffect(() => {
    setLocalSessionId(filters.sessionIdSearch || "");
  }, [filters.sessionIdSearch]);

  useEffect(() => {
    setLocalConversationId(filters.conversationIdSearch || "");
  }, [filters.conversationIdSearch]);

  const handleTimeRangeChange = useCallback(
    (value: TimeRangePreset | "") => {
      const newFilters = { ...filters };
      if (value === "") {
        delete newFilters.timeRangePreset;
        delete newFilters.timeFrom;
        delete newFilters.timeTo;
      } else {
        newFilters.timeRangePreset = value;
        if (value !== "custom") {
          delete newFilters.timeFrom;
          delete newFilters.timeTo;
        }
      }
      setShowCustomDates(value === "custom");
      onFiltersChange(newFilters);
    },
    [filters, onFiltersChange]
  );

  const handleOrganizationChange = useCallback(
    (_: unknown, value: OrganizationOption | null) => {
      const newFilters = { ...filters };
      if (value) {
        newFilters.organizationId = value.id;
      } else {
        delete newFilters.organizationId;
      }
      onFiltersChange(newFilters);
    },
    [filters, onFiltersChange]
  );

  const handleAssessmentChange = useCallback(
    (_: unknown, value: ExperienceOption | null) => {
      const newFilters = { ...filters };
      if (value) {
        newFilters.assessmentKey = value.resourceKey;
        // Auto-select the organization if not already selected
        if (!newFilters.organizationId) {
          newFilters.organizationId = value.organizationId;
        }
      } else {
        delete newFilters.assessmentKey;
      }
      onFiltersChange(newFilters);
    },
    [filters, onFiltersChange]
  );

  const handleRoleplayChange = useCallback(
    (_: unknown, value: ExperienceOption | null) => {
      const newFilters = { ...filters };
      if (value) {
        newFilters.roleplayKey = value.resourceKey;
        // Auto-select the organization if not already selected
        if (!newFilters.organizationId) {
          newFilters.organizationId = value.organizationId;
        }
      } else {
        delete newFilters.roleplayKey;
      }
      onFiltersChange(newFilters);
    },
    [filters, onFiltersChange]
  );

  // Debounced user email search
  const handleUserSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setLocalUserEmail(value);

      // Clear existing timer
      if (userEmailTimerRef.current) {
        clearTimeout(userEmailTimerRef.current);
      }

      // Set new timer
      userEmailTimerRef.current = setTimeout(() => {
        const newFilters = { ...filters };
        if (value) {
          newFilters.userEmailSearch = value;
        } else {
          delete newFilters.userEmailSearch;
        }
        onFiltersChange(newFilters);
      }, SEARCH_DEBOUNCE_MS);
    },
    [filters, onFiltersChange]
  );

  // Debounced session ID search
  const handleSessionSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setLocalSessionId(value);

      if (sessionIdTimerRef.current) {
        clearTimeout(sessionIdTimerRef.current);
      }

      sessionIdTimerRef.current = setTimeout(() => {
        const newFilters = { ...filters };
        if (value) {
          newFilters.sessionIdSearch = value;
        } else {
          delete newFilters.sessionIdSearch;
        }
        onFiltersChange(newFilters);
      }, SEARCH_DEBOUNCE_MS);
    },
    [filters, onFiltersChange]
  );

  // Debounced conversation ID search
  const handleConversationSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setLocalConversationId(value);

      if (conversationIdTimerRef.current) {
        clearTimeout(conversationIdTimerRef.current);
      }

      conversationIdTimerRef.current = setTimeout(() => {
        const newFilters = { ...filters };
        if (value) {
          newFilters.conversationIdSearch = value;
        } else {
          delete newFilters.conversationIdSearch;
        }
        onFiltersChange(newFilters);
      }, SEARCH_DEBOUNCE_MS);
    },
    [filters, onFiltersChange]
  );

  // Status filter handler
  const handleStatusChange = useCallback(
    (event: SelectChangeEvent<string[]>) => {
      const value = event.target.value;
      const newFilters = { ...filters };
      if (value && value.length > 0) {
        newFilters.status = typeof value === "string" ? value.split(",") : value;
      } else {
        delete newFilters.status;
      }
      onFiltersChange(newFilters);
    },
    [filters, onFiltersChange]
  );

  // Codec filter handler
  const handleCodecChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const value = event.target.value;
      const newFilters = { ...filters };
      if (value) {
        newFilters.codec = value;
      } else {
        delete newFilters.codec;
      }
      onFiltersChange(newFilters);
    },
    [filters, onFiltersChange]
  );

  const handleIncludeTestInfraChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newFilters = { ...filters, includeTestInfra: event.target.checked };
      onFiltersChange(newFilters);
    },
    [filters, onFiltersChange]
  );

  const handleDateFromChange = useCallback(
    (date: Date | null) => {
      const newFilters = { ...filters };
      if (date) {
        newFilters.timeFrom = date.toISOString();
      } else {
        delete newFilters.timeFrom;
      }
      onFiltersChange(newFilters);
    },
    [filters, onFiltersChange]
  );

  const handleDateToChange = useCallback(
    (date: Date | null) => {
      const newFilters = { ...filters };
      if (date) {
        newFilters.timeTo = date.toISOString();
      } else {
        delete newFilters.timeTo;
      }
      onFiltersChange(newFilters);
    },
    [filters, onFiltersChange]
  );

  const handleClearFilters = useCallback(() => {
    // Clear debounce timers
    if (userEmailTimerRef.current) clearTimeout(userEmailTimerRef.current);
    if (sessionIdTimerRef.current) clearTimeout(sessionIdTimerRef.current);
    if (conversationIdTimerRef.current) clearTimeout(conversationIdTimerRef.current);

    // Reset local state
    setLocalUserEmail("");
    setLocalSessionId("");
    setLocalConversationId("");

    onFiltersChange({});
    setShowCustomDates(false);
  }, [onFiltersChange]);

  const hasActiveFilters =
    filters.timeRangePreset ||
    filters.organizationId ||
    filters.assessmentKey ||
    filters.roleplayKey ||
    filters.userEmailSearch ||
    filters.sessionIdSearch ||
    filters.conversationIdSearch ||
    filters.includeTestInfra === false ||
    (filters.status && filters.status.length > 0) ||
    filters.codec;

  const selectedOrganization = organizations.find(
    (org) => org.id === filters.organizationId
  );
  const selectedAssessment = assessments.find(
    (a) => a.resourceKey === filters.assessmentKey
  );
  const selectedRoleplay = roleplays.find(
    (r) => r.resourceKey === filters.roleplayKey
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          mb: 2,
          p: 2,
          bgcolor: "#f8f9fa",
          borderRadius: 1,
          border: "1px solid #e0e0e0",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: expanded ? 2 : 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FilterListIcon color="action" />
            <Box sx={{ fontWeight: 500, color: "#424242" }}>Filters</Box>
            {hasActiveFilters && (
              <Chip
                label={`${Object.keys(filters).filter((k) => filters[k as keyof SessionFiltersType] !== undefined && filters[k as keyof SessionFiltersType] !== true).length} active`}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {hasActiveFilters && (
              <Button
                size="small"
                startIcon={<ClearIcon />}
                onClick={handleClearFilters}
                disabled={loading}
              >
                Clear
              </Button>
            )}
            <IconButton size="small" onClick={() => setExpanded(!expanded)}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        {/* Filter Controls */}
        <Collapse in={expanded}>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              alignItems: "flex-start",
            }}
          >
            {/* Time Range */}
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Time Range</InputLabel>
              <Select
                value={filters.timeRangePreset || ""}
                label="Time Range"
                onChange={(e) =>
                  handleTimeRangeChange(e.target.value as TimeRangePreset | "")
                }
                disabled={loading}
              >
                {TIME_RANGE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Custom Date Pickers */}
            {showCustomDates && (
              <>
                <DateTimePicker
                  label="From"
                  value={filters.timeFrom ? new Date(filters.timeFrom) : null}
                  onChange={handleDateFromChange}
                  disabled={loading}
                  slotProps={{
                    textField: { size: "small", sx: { width: 200 } },
                  }}
                />
                <DateTimePicker
                  label="To"
                  value={filters.timeTo ? new Date(filters.timeTo) : null}
                  onChange={handleDateToChange}
                  disabled={loading}
                  slotProps={{
                    textField: { size: "small", sx: { width: 200 } },
                  }}
                />
              </>
            )}

            {/* Organization */}
            <Autocomplete
              options={organizations}
              getOptionLabel={(opt) => opt.name}
              value={selectedOrganization || null}
              onChange={handleOrganizationChange}
              disabled={loading}
              renderInput={(params) => (
                <TextField {...params} label="Organization" size="small" />
              )}
              sx={{ minWidth: 200 }}
            />

            {/* Assessment */}
            <Autocomplete
              options={assessments.filter(
                (a) =>
                  !filters.organizationId ||
                  a.organizationId === filters.organizationId
              )}
              getOptionLabel={(opt) => opt.name}
              value={selectedAssessment || null}
              onChange={handleAssessmentChange}
              disabled={loading || loadingExperiences}
              loading={loadingExperiences}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterOptions={(options, { inputValue }) => {
                const lowerInput = inputValue.toLowerCase();
                return options.filter((opt) => {
                  const org = organizations.find((o) => o.id === opt.organizationId);
                  const orgName = org?.name?.toLowerCase() || "";
                  return (
                    opt.name.toLowerCase().includes(lowerInput) ||
                    opt.resourceKey.toLowerCase().includes(lowerInput) ||
                    orgName.includes(lowerInput)
                  );
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Assessment"
                  size="small"
                  placeholder={loadingExperiences ? "Loading..." : "Search by name or org..."}
                />
              )}
              renderOption={(props, option) => {
                const org = organizations.find((o) => o.id === option.organizationId);
                return (
                  <Box component="li" {...props} key={option.id}>
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Box sx={{ fontWeight: 500 }}>{option.name}</Box>
                      <Box sx={{ fontSize: "0.75rem", color: "#666" }}>
                        {org?.name || option.organizationId.substring(0, 8) + "..."}
                      </Box>
                    </Box>
                  </Box>
                );
              }}
              sx={{ minWidth: 250 }}
            />

            {/* Roleplay */}
            <Autocomplete
              options={roleplays.filter(
                (r) =>
                  !filters.organizationId ||
                  r.organizationId === filters.organizationId
              )}
              getOptionLabel={(opt) => opt.name}
              value={selectedRoleplay || null}
              onChange={handleRoleplayChange}
              disabled={loading || loadingExperiences}
              loading={loadingExperiences}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterOptions={(options, { inputValue }) => {
                const lowerInput = inputValue.toLowerCase();
                return options.filter((opt) => {
                  const org = organizations.find((o) => o.id === opt.organizationId);
                  const orgName = org?.name?.toLowerCase() || "";
                  return (
                    opt.name.toLowerCase().includes(lowerInput) ||
                    opt.resourceKey.toLowerCase().includes(lowerInput) ||
                    orgName.includes(lowerInput)
                  );
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Roleplay"
                  size="small"
                  placeholder={loadingExperiences ? "Loading..." : "Search by name or org..."}
                />
              )}
              renderOption={(props, option) => {
                const org = organizations.find((o) => o.id === option.organizationId);
                return (
                  <Box component="li" {...props} key={option.id}>
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Box sx={{ fontWeight: 500 }}>{option.name}</Box>
                      <Box sx={{ fontSize: "0.75rem", color: "#666" }}>
                        {org?.name || option.organizationId.substring(0, 8) + "..."}
                      </Box>
                    </Box>
                  </Box>
                );
              }}
              sx={{ minWidth: 250 }}
            />

            {/* Status Filter */}
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                multiple
                value={filters.status || []}
                label="Status"
                onChange={handleStatusChange}
                disabled={loading}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {(selected as string[]).map((value) => {
                      const option = STATUS_OPTIONS.find((o) => o.value === value);
                      return (
                        <Chip
                          key={value}
                          label={option?.label || value}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: "0.7rem",
                            bgcolor: option?.color,
                            color: "#fff",
                          }}
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          bgcolor: option.color,
                        }}
                      />
                      {option.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Codec Filter */}
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Codec</InputLabel>
              <Select
                value={filters.codec || ""}
                label="Codec"
                onChange={handleCodecChange}
                disabled={loading}
              >
                {CODEC_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* User Search */}
            <TextField
              label="User Email"
              size="small"
              value={localUserEmail}
              onChange={handleUserSearchChange}
              disabled={loading}
              sx={{ minWidth: 180 }}
              placeholder="Search by email..."
            />

            {/* Session ID Search */}
            <TextField
              label="Session ID"
              size="small"
              value={localSessionId}
              onChange={handleSessionSearchChange}
              disabled={loading}
              sx={{ minWidth: 140 }}
              placeholder="Partial match..."
            />

            {/* Conversation ID Search */}
            <TextField
              label="Conversation ID"
              size="small"
              value={localConversationId}
              onChange={handleConversationSearchChange}
              disabled={loading}
              sx={{ minWidth: 140 }}
              placeholder="Partial match..."
            />

            {/* Include Test Infra */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.includeTestInfra !== false}
                  onChange={handleIncludeTestInfraChange}
                  disabled={loading}
                  size="small"
                />
              }
              label="Include Test"
              sx={{ ml: 1 }}
            />
          </Box>
        </Collapse>
      </Box>
    </LocalizationProvider>
  );
};

export default SessionFilters;
